import type { ReactNode } from "react";

import {
  buildOutstandingRoundLine,
  CONFIRM_GATE_CONSEQUENCE,
  CONFIRM_GATE_NO_WINNER_LINE,
  CONFIRM_GATE_SEASON_TROPHY_LINE,
  CONFIRM_GATE_SPLIT_MEDAL_SUBTITLE,
} from "./confirmGateCopy";
import { ConfirmGateOutstandingGames } from "./ConfirmGateOutstandingGames";
import type {
  typeConfirmGateEntrant,
  typeConfirmGateOutcomeShape,
  typeConfirmGateOutstandingRound,
} from "./confirmGateState";
import { ConfirmGateWinnerRow } from "./ConfirmGateWinnerRow";

export type typeConfirmGateOutcomeStepProps = {
  shape: typeConfirmGateOutcomeShape;
  winners: readonly typeConfirmGateEntrant[];
  medalName: string;
  isSeasonLastContest: boolean;
  /** The string from `resolveNamingWindowWarning`, or null when no window is open. */
  namingWindowWarning: string | null;
  /**
   * ISSUE-868. The round that is still ungraded, or null when there is none.
   * Optional so the call sites that never carry one stay unchanged.
   */
  outstandingRound?: typeConfirmGateOutstandingRound | null;
  /**
   * ISSUE-868. The label of the round `outstandingRound` points at, from
   * `/round/list`. Null when it could not be resolved, in which case the line
   * still renders and simply names no round.
   */
  outstandingRoundLabel?: string | null;
};

export const ConfirmGateOutcomeStep = ({
  shape,
  winners,
  medalName,
  isSeasonLastContest,
  namingWindowWarning,
  outstandingRound = null,
  outstandingRoundLabel = null,
}: typeConfirmGateOutcomeStepProps): ReactNode => {
  return (
    <div
      className="flex flex-col gap-3"
      data-testid="confirm-gate-outcome-step"
    >
      {shape === "splitMedal" ? (
        <p data-testid="confirm-gate-tie-subtitle">
          {CONFIRM_GATE_SPLIT_MEDAL_SUBTITLE}
        </p>
      ) : null}
      {shape === "noWinner" ? (
        <p data-testid="confirm-gate-no-winner-line">
          {CONFIRM_GATE_NO_WINNER_LINE}
        </p>
      ) : null}
      {shape !== "noWinner"
        ? winners.map((winner) => (
            <ConfirmGateWinnerRow
              key={winner.entryId}
              displayName={winner.displayName}
              medalName={medalName}
            />
          ))
        : null}
      {namingWindowWarning !== null ? (
        <p
          data-testid="confirm-gate-naming-warning"
          className="text-caption text-warning"
        >
          {namingWindowWarning}
        </p>
      ) : null}
      {isSeasonLastContest ? (
        <p
          data-testid="confirm-gate-trophy-line"
          className="text-body font-bold text-ink"
        >
          {CONFIRM_GATE_SEASON_TROPHY_LINE}
        </p>
      ) : null}
      {outstandingRound !== null ? (
        <>
          <p
            data-testid="confirm-gate-outstanding-round"
            data-round-id={outstandingRound.roundId}
            className="text-body text-warning"
          >
            {buildOutstandingRoundLine({
              gameCount: outstandingRound.eventIds.length,
              roundLabel: outstandingRoundLabel,
            })}
          </p>
          <ConfirmGateOutstandingGames eventIds={outstandingRound.eventIds} />
        </>
      ) : null}
      <p
        data-testid="confirm-gate-consequence"
        className="text-body text-ink-secondary"
      >
        {CONFIRM_GATE_CONSEQUENCE}
      </p>
    </div>
  );
};
